<script setup lang="ts">
import type { ITelegramStatus } from '@/entities/auth'
import { ElMessage } from 'element-plus'
import { computed, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  useDisconnectTelegramMutation,
  useFetchProfileQuery,
} from '@/entities/auth'
import CButton from './CButton.vue'

/**
 * Inline Telegram connection panel.
 *
 * Three visible states, driven by the profile.telegram payload from the API:
 *  - not connected → Connect button (opens bot deep-link + auto-polls profile)
 *  - connected & active → username (read-only) + Disconnect button
 *  - connected & inactive → username + "bot blocked" warning + Reconnect + Disconnect
 *
 * No props — the component reads everything it needs from the auth profile query.
 * Drop it anywhere in the app where the user can manage their bot link.
 */

const { t } = useI18n()

const profileQuery = useFetchProfileQuery()
const { mutateAsync: disconnectTelegram, isPending: isDisconnecting }
  = useDisconnectTelegramMutation()

const profile = computed(() => profileQuery.data.value)
const telegram = computed<ITelegramStatus>(() => profile.value?.telegram ?? {
  connected: false,
  chat_id: null,
  username: null,
  display_name: null,
  active: false,
  bot_username: '',
})

// Bot username comes from the backend (settings.TELEGRAM_BOT_USERNAME → profile.telegram.bot_username)
// so whatever bot this backend is wired to is the bot we deep-link to. Single source of truth.
const botStartUrl = computed(() => {
  const botUsername = (telegram.value.bot_username || '').replace(/^@/, '')
  if (!botUsername)
    return ''
  const base = `https://t.me/${botUsername}`
  const userId = profile.value?.id
  return userId ? `${base}?start=${userId}` : base
})

// Polling state — set while we're waiting for the user to come back from Telegram.
const isPolling = ref(false)
let pollTimer: ReturnType<typeof setInterval> | null = null
let pollDeadline = 0
const POLL_INTERVAL_MS = 2000
const POLL_WINDOW_MS = 60_000

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
  isPolling.value = false
}

async function checkConnection() {
  // Refetch profile and check the latest telegram status.
  const result = await profileQuery.refetch()
  const next = result.data?.telegram
  if (next?.connected && next.active) {
    stopPolling()
    ElMessage.success(t('telegram_connection_success'))
    return
  }
  if (Date.now() > pollDeadline) {
    stopPolling()
  }
}

function startPolling() {
  if (pollTimer)
    return
  isPolling.value = true
  pollDeadline = Date.now() + POLL_WINDOW_MS
  pollTimer = setInterval(checkConnection, POLL_INTERVAL_MS)
}

function handleConnect() {
  // Guard against opening an empty URL while the profile (and bot_username) is still loading.
  if (!botStartUrl.value)
    return
  // Always open the deep-link in a new tab so the panel stays visible to show progress.
  window.open(botStartUrl.value, '_blank', 'noopener,noreferrer')
  startPolling()
}

async function handleDisconnect() {
  try {
    await disconnectTelegram()
    ElMessage.success(t('telegram_disconnected'))
    stopPolling()
  }
  catch (err) {
    console.error('Failed to disconnect Telegram:', err)
    ElMessage.error(t('failed_to_disconnect_telegram'))
  }
}

onBeforeUnmount(stopPolling)

// Display name from Telegram (first + last). May be null if /start hasn't run since the field was added.
const accountDisplayName = computed(() => telegram.value.display_name || '')

// Handle string: @username when present, otherwise the numeric chat id.
const accountHandle = computed(() => {
  if (telegram.value.username)
    return `@${telegram.value.username}`
  if (telegram.value.chat_id)
    return `ID: ${telegram.value.chat_id}`
  return ''
})
</script>

<template>
  <div
    class="bg-primary-light-9 border border-primary-light rounded-lg p-3 sm:p-4 flex flex-col gap-3 w-full"
  >
    <!-- Not connected -->
    <template v-if="!telegram.connected">
      <div class="flex items-center gap-3">
        <i class="icon-telegram text-2xl text-brand-violet" />
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-text-regular">
            {{ t("telegram_not_connected") }}
          </p>
          <p class="text-xs text-text-secondary">
            {{ t("telegram_connect_hint") }}
          </p>
        </div>
      </div>
      <CButton
        type="primary"
        class="w-full sm:w-auto self-start"
        :loading="isPolling"
        @click="handleConnect"
      >
        <i class="icon-telegram text-xl" />
        <span class="ml-2">
          {{ isPolling ? t("telegram_waiting_for_link") : t("connect_telegram") }}
        </span>
      </CButton>
    </template>

    <!-- Connected (active or inactive) -->
    <template v-else>
      <div class="flex items-center gap-3">
        <i class="icon-telegram text-2xl text-brand-violet" />
        <div class="flex-1 min-w-0">
          <p
            v-if="accountDisplayName"
            class="text-sm font-medium text-text-regular truncate"
          >
            {{ accountDisplayName }}
          </p>
          <p
            v-if="accountHandle"
            class="text-xs text-text-secondary truncate"
            :class="{ 'text-sm font-medium text-text-regular': !accountDisplayName }"
          >
            {{ accountHandle }}
          </p>
          <p
            class="text-xs"
            :class="telegram.active ? 'text-success' : 'text-danger'"
          >
            <template v-if="telegram.active">
              <i class="icon-check text-sm" />
              {{ t("telegram_status_active") }}
            </template>
            <template v-else>
              <i class="icon-alert-triangle text-sm" />
              {{ t("telegram_status_blocked") }}
            </template>
          </p>
        </div>
      </div>

      <div class="flex flex-wrap gap-2">
        <CButton
          v-if="!telegram.active"
          type="primary"
          :loading="isPolling"
          @click="handleConnect"
        >
          <i class="icon-telegram text-xl" />
          <span class="ml-2">
            {{ isPolling ? t("telegram_waiting_for_link") : t("telegram_reconnect") }}
          </span>
        </CButton>
        <CButton
          variant="ghost"
          class="!bg-white !border-border-light !text-text-secondary"
          :loading="isDisconnecting"
          @click="handleDisconnect"
        >
          {{ t("telegram_disconnect") }}
        </CButton>
      </div>
    </template>
  </div>
</template>
